import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginBrand } from './auth/LoginBrand';
import { VerifyingOverlay } from './ui/VerifyingOverlay';
import { canAccessAdminHub } from '../lib/adminHubPermissions';
import { formatAuthErrorMessage } from '../lib/formatAuthError';
import {
  isCalendarPath,
  isChooserVerifyingHold,
  isSoftPortalEntryPath,
  isSoftPortalEntryPending,
  isSoftPortalSwitchActive,
  portalKindFromPath,
  readPortalChoice,
  rememberPostLoginReturn,
  softPortalEntryTarget,
} from '../lib/portal';
import { portalSideAccess } from '../lib/portalAccess';

function isAdminPath(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const {
    session,
    loading,
    configured,
    accessStatus,
    authError,
    signOut,
    refreshProfile,
    portalAccess,
  } = useAuth();
  const location = useLocation();
  const adminHubAllowed = isAdminPath(location.pathname) && canAccessAdminHub(portalAccess);
  const calendarAllowed = isCalendarPath(location.pathname);
  const pathKind = portalKindFromPath(location.pathname);
  const softPending = isSoftPortalEntryPending();
  const softEntry =
    softPending ||
    isSoftPortalEntryPath(location.pathname) ||
    isSoftPortalSwitchActive(location.pathname) ||
    (pathKind != null &&
      (softPortalEntryTarget() === pathKind || readPortalChoice() === pathKind));

  if (!configured) {
    rememberPostLoginReturn(location.pathname, location.search, location.hash);
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  /**
   * Soft choose→portal / PortalSwitcher: never bounce to Login.
   * On /choose-portal, keep the workspace grid and let PortalChoosePage show the
   * verifying modal. Elsewhere, dim the current shell with the same overlay.
   */
  if (softEntry || softPending) {
    if (accessStatus === 'checking') {
      // Choose-portal owns the overlay. Skip a second one while choose→portal is finishing
      // (destination must not flash Verifying again on mobile CRM).
      if (location.pathname === '/choose-portal' || isChooserVerifyingHold()) {
        return children;
      }
      return (
        <>
          {children}
          <VerifyingOverlay message={authError ? `Verifying failed — ${authError}` : undefined} />
        </>
      );
    }
    if (pathKind && portalAccess && !portalSideAccess(portalAccess, pathKind).hasAccess) {
      return <Navigate to="/choose-portal" replace />;
    }
    return children;
  }

  /**
   * Portal path the user already has allowlist access to — same as soft entry:
   * never flash Verifying when the workspace is already authorized.
   */
  if (
    session &&
    pathKind &&
    portalAccess &&
    (accessStatus === 'allowed' ||
      accessStatus === 'choose_portal' ||
      accessStatus === 'checking' ||
      accessStatus === 'idle')
  ) {
    const side = portalSideAccess(portalAccess, pathKind);
    if (!side.hasAccess) {
      return <Navigate to="/choose-portal" replace />;
    }
    return children;
  }

  if (loading || accessStatus === 'checking') {
    // Choose→portal owns a single Verifying on the chooser. Never replace the
    // destination shell with a second login+Verifying flash (common on mobile CRM).
    if (location.pathname === '/choose-portal' || isChooserVerifyingHold()) {
      return children;
    }
    return (
      <div className="login-page">
        <div className="login-card">
          <LoginBrand />
          <div className="login-card__body">
            <p className="login-card__lead">Sign in with your company Microsoft account</p>
            {authError ? (
              <div className="alert alert-error">{formatAuthErrorMessage(authError)}</div>
            ) : null}
          </div>
        </div>
        <VerifyingOverlay />
      </div>
    );
  }

  if (!session) {
    rememberPostLoginReturn(location.pathname, location.search, location.hash);
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (session && accessStatus === 'idle') {
    if (authError) {
      return (
        <div className="login-page">
          <div className="login-card">
            <LoginBrand />
            <div className="login-card__body">
              <div className="alert alert-error">{formatAuthErrorMessage(authError)}</div>
              <div className="login-page__actions">
                <button type="button" className="btn-primary" onClick={() => void refreshProfile()}>
                  Retry
                </button>
                <button type="button" className="btn-secondary" onClick={() => void signOut()}>
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="login-page">
        <div className="login-card">
          <LoginBrand />
          <div className="login-card__body">
            <p className="login-card__lead">Sign in with your company Microsoft account</p>
          </div>
        </div>
        <VerifyingOverlay />
      </div>
    );
  }

  if (accessStatus === 'not_on_allowlist' || accessStatus === 'no_email') {
    return <Navigate to="/not-authorized" replace />;
  }

  if (accessStatus === 'choose_portal') {
    if (adminHubAllowed || calendarAllowed) {
      return children;
    }
    if (location.pathname !== '/choose-portal') {
      return <Navigate to="/choose-portal" replace />;
    }
    return children;
  }

  if (accessStatus !== 'allowed') {
    return (
      <div className="login-page">
        <p className="page-lead">Unable to verify CRM access. Please try again.</p>
        {authError && <p className="alert alert-error">{authError}</p>}
        <button type="button" className="btn-primary" onClick={() => void refreshProfile()}>
          Retry
        </button>
      </div>
    );
  }

  return children;
}
